/**
 * B1c.3.2d — Side-by-side: expected selected-month rent vs. CUMULATIVE Bexio
 * actuals. This is NOT a Soll-Ist-Abgleich, NOT a monthly reconciliation, and NOT
 * a delta (SAFETY_HOOKS §9.12).
 *
 * Hard rule: the component ACTIVELY reads the backend comparability guard
 * ``direct_delta_available``. In this mode it is always ``false``, so NO
 * comparison UI is rendered — no delta/gap column, no expected-minus-actual, no
 * traffic-light / red-green, no arrows, no percent-of-expected, no over/under
 * colour coding. Comparison rendering is GATED on ``canShowDelta`` so a future
 * true-mode would require explicit handling, never accidental rendering.
 *
 * Half-case rule: a missing side is a DATA STATE, not a comparable amount. An
 * expected-only tenancy shows "kein Ist / nicht gemappt" (never a bare CHF 0 that
 * reads as "nothing invoiced"); an actual-only tenancy shows "kein Soll". Neither
 * creates a fake gap/delta.
 *
 * Money arrives as JSON decimal strings; fmtMoney parses without clamping. The two
 * sides are shown as INDEPENDENT figures with their own scope labels (expected =
 * selected month; Bexio = cumulative).
 */
import React from "react";

function fmtMoney(v, currency) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${currency || "CHF"} ${n.toLocaleString("de-CH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDateTime(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("de-CH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function Badge({ children, tone = "neutral" }) {
  const tones = {
    neutral: "border-input text-muted-foreground",
    good: "border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400",
    warn: "border-amber-200 dark:border-amber-500/30 text-amber-800 dark:text-amber-300",
    bad: "border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-300",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.05em] ${
        tones[tone] || tones.neutral
      }`}
    >
      {children}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="rounded-[10px] border border-border bg-card p-4">
      <div className="text-[10px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-[18px] font-bold text-foreground">{value}</div>
    </div>
  );
}

const _MONTHS_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

function MonthSelector({ year, month, onMonthChange, disabled }) {
  const now = new Date();
  const thisYear = now.getFullYear();
  const years = [thisYear - 2, thisYear - 1, thisYear, thisYear + 1];
  return (
    <div className="flex items-center gap-2 text-[12px] text-foreground">
      <label className="text-muted-foreground">Erwarteter Monat:</label>
      <select
        aria-label="Monat"
        className="rounded-[8px] border border-input bg-card px-2 py-1 text-[12px] text-foreground"
        value={month}
        disabled={disabled}
        onChange={(e) => onMonthChange(year, Number(e.target.value))}
      >
        {_MONTHS_DE.map((m, i) => (
          <option key={i + 1} value={i + 1}>
            {m}
          </option>
        ))}
      </select>
      <select
        aria-label="Jahr"
        className="rounded-[8px] border border-input bg-card px-2 py-1 text-[12px] text-foreground"
        value={year}
        disabled={disabled}
        onChange={(e) => onMonthChange(Number(e.target.value), month)}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
}

// A row's Expected/Soll side is "present" ONLY if a real expected value/signal
// exists — a recurring OR one_time amount, or the recurring-revenue flag. A
// WARNING (e.g. missing_tenancy_revenue) must NEVER make Expected present: a
// zero-amount "payment-liable but no rent" row must render the explicit "kein
// Soll" state, not a bare "CHF 0.00". (B1c.3.2d patch — false-green fix.)
// Note: recurring==0 with one_time>0 IS Expected context (a selected-month
// one-time amount), so it correctly does NOT read as "kein Soll".
function _hasExpected(r) {
  return (
    r.expected_has_recurring_revenue === true ||
    Number(r.expected_selected_month_recurring_chf) !== 0 ||
    Number(r.expected_selected_month_one_time_chf) !== 0
  );
}

// Robust warning detection. The backend emits each half-case key at BOTH levels
// (verified: bexio_expected_vs_invoiced_service.py — row-level
// row.expected_warnings :219/:228, top-level warnings.<key> tenancy_id lists
// :299/:300). Support both so either shape alone still drives the label.
function _warned(row, key, idSet) {
  return (row.expected_warnings || []).includes(key) || idSet.has(row.tenancy_id);
}

function Table({ rows, currency, warnings }) {
  const list = Array.isArray(rows) ? rows : [];
  const noBexioIds = new Set((warnings && warnings.no_bexio_invoice_context) || []);
  const missingSollIds = new Set(
    (warnings && warnings.missing_tenancy_revenue) || []
  );
  if (list.length === 0) {
    return (
      <div className="rounded-[10px] border border-border bg-card p-6 text-center text-[12px] text-muted-foreground">
        Keine Daten für den gewählten Monat.
      </div>
    );
  }
  const head = [
    "Mieter / Referenz",
    "Einheit / Zimmer",
    "Erwartet Miete (gewählter Monat)",
    "Erwartet einmalig (gewählter Monat)",
    "Bexio fakturiert (kumuliert)",
    "Bexio bezahlt (kumuliert)",
    "Bexio offen (kumuliert)",
    "Hinweise",
  ];
  return (
    <div className="overflow-x-auto rounded-[10px] border border-border">
      <table className="w-full border-collapse text-[12px]">
        <thead>
          <tr className="bg-card">
            {head.map((h, i) => (
              <th
                key={i}
                className="whitespace-nowrap px-3 py-2 text-left text-[10px] font-medium uppercase tracking-[0.06em] text-muted-foreground"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {list.map((r) => {
            // Precedence: "kein Soll" is driven STRICTLY by _hasExpected (real
            // amounts), never by a warning — a real non-zero amount is never
            // hidden. The missingSoll flag only feeds the Hinweis badge.
            const noExpected = !_hasExpected(r);
            const noBexio = _warned(r, "no_bexio_invoice_context", noBexioIds);
            const missingSoll = _warned(r, "missing_tenancy_revenue", missingSollIds);
            const oneTime = Number(r.expected_selected_month_one_time_chf);
            return (
              <tr
                key={r.tenancy_id}
                className="border-t border-border align-top text-foreground"
              >
                <td className="px-3 py-2">
                  <div className="font-semibold text-foreground">
                    {r.tenant_name || "—"}
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.tenancy_reference_number || "—"}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div>{r.unit_label || "—"}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {r.room_label || "—"}
                  </div>
                </td>
                {/* Expected side — SELECTED MONTH. Missing side = explicit label. */}
                <td className="whitespace-nowrap px-3 py-2">
                  {noExpected ? (
                    <Badge tone="neutral">kein Soll</Badge>
                  ) : (
                    fmtMoney(r.expected_selected_month_recurring_chf, currency)
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {noExpected ? "—" : oneTime !== 0
                    ? fmtMoney(r.expected_selected_month_one_time_chf, currency)
                    : "—"}
                </td>
                {/* Actual side — CUMULATIVE. Missing side = explicit label. */}
                <td className="whitespace-nowrap px-3 py-2">
                  {noBexio ? (
                    <Badge tone="warn">kein Ist / nicht gemappt</Badge>
                  ) : (
                    fmtMoney(r.bexio_cumulative_invoiced_chf, currency)
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {noBexio ? "—" : fmtMoney(r.bexio_cumulative_paid_chf, currency)}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {noBexio ? "—" : fmtMoney(r.bexio_cumulative_open_chf, currency)}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {missingSoll ? (
                      <Badge tone="warn">kein Soll (tenancy_revenue fehlt)</Badge>
                    ) : null}
                    {noBexio ? (
                      <Badge tone="neutral">nicht gemappt</Badge>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function BexioExpectedVsInvoicedSection({
  data,
  loading,
  error,
  onRetry,
  year,
  month,
  onMonthChange,
}) {
  const malformed = data && (!data.summary || !data.rows || !data.comparison_mode);
  const isError = !!error || (!loading && (!data || malformed));

  // Comparability guard — comparison UI is GATED on this flag. Only an explicit
  // `true` would ever enable future comparison UI; the current B1c.3.2c mode
  // returns false, so no comparison UI is rendered.
  const canShowDelta = data?.direct_delta_available === true;
  const currency = data?.summary?.currency || "CHF";

  return (
    <section className="mb-6">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-[14px] font-semibold text-foreground">
            Erwartete Miete ↔ Bexio (Side-by-side, kein Monatsabgleich)
          </h2>
          <p className="max-w-[760px] text-[11px] text-muted-foreground">
            Gegenüberstellung ohne Vergleich: links die Vertrags-Sollmiete des
            gewählten Monats, rechts die kumulierten Bexio-Ist-Werte über alle
            gemappten Rechnungen. Unterschiedliche Zeitbezüge — kein Delta.
          </p>
        </div>
        <MonthSelector
          year={year}
          month={month}
          onMonthChange={onMonthChange}
          disabled={loading}
        />
      </div>

      {/* Fixed "not a monthly reconciliation" banner. */}
      <div className="mb-3 rounded-[10px] border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3 text-[12px] text-amber-800 dark:text-amber-300">
        Side-by-side, <strong>kein Monatsabgleich</strong>. Soll (gewählter Monat)
        und Ist (kumuliert) sind nicht direkt vergleichbar; es wird bewusst kein
        Delta berechnet.
      </div>

      {loading ? (
        <div className="flex items-center gap-2 rounded-[10px] border border-border bg-card p-6 text-[12px] text-muted-foreground">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-input border-t-primary" />
          Lädt Soll/Ist-Gegenüberstellung …
        </div>
      ) : isError ? (
        <div className="rounded-[10px] border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-4 text-[13px] text-red-700 dark:text-red-300">
          <div>
            Gegenüberstellung konnte nicht geladen werden{error ? `: ${error}` : "."}
          </div>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 rounded-[8px] border border-red-200 dark:border-red-500/30 px-3 py-1 text-[12px] text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-500/20"
            >
              Erneut versuchen
            </button>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Backend semantic notice — always surfaced verbatim. */}
          {data.semantic_notice ? (
            <div className="rounded-[10px] border border-border bg-card p-3 text-[11px] text-muted-foreground">
              {data.semantic_notice}
            </div>
          ) : null}

          {/* Comparability note derived from direct_delta_available (gated). */}
          {!canShowDelta ? (
            <div className="text-[11px] text-muted-foreground">
              Kein direkter Vergleich/Delta verfügbar (direct_delta_available =
              false) — die beiden Seiten haben unterschiedliche Zeitbezüge.
            </div>
          ) : null}

          {/* Technical guard details. */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
            <span>comparison_mode: {data.comparison_mode}</span>
            <span>expected_scope: {data.expected_scope}</span>
            <span>actual_scope: {data.actual_scope}</span>
            <span>actual_period: {data.actual_period == null ? "null" : data.actual_period}</span>
            <span>direct_delta_available: {String(!!data.direct_delta_available)}</span>
            <span>Stand: {fmtDateTime(data.generated_at)}</span>
          </div>

          {/* Summary cards. */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            <StatCard
              label="Erwartet Miete (gewählter Monat)"
              value={fmtMoney(data.summary.expected_selected_month_recurring_chf, currency)}
            />
            <StatCard
              label="Erwartet einmalig (gewählter Monat)"
              value={fmtMoney(data.summary.expected_selected_month_one_time_chf, currency)}
            />
            <StatCard
              label="Bexio fakturiert (kumuliert)"
              value={fmtMoney(data.summary.bexio_cumulative_invoiced_chf, currency)}
            />
            <StatCard
              label="Bexio bezahlt (kumuliert)"
              value={fmtMoney(data.summary.bexio_cumulative_paid_chf, currency)}
            />
            <StatCard
              label="Bexio offen (kumuliert)"
              value={fmtMoney(data.summary.bexio_cumulative_open_chf, currency)}
            />
          </div>

          <Table rows={data.rows} currency={currency} warnings={data.warnings} />
        </div>
      )}
    </section>
  );
}
